import { useEffect, useState } from 'react';
import { readLocal, writeLocal } from '@ndrc/app-sdk/client';
import { ActionMenu } from '@ndrc/ui';
import { t } from '../messages';
import { useRef } from 'react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import { useWindowFrame } from '../desk/context';
import { TitleBar } from './TitleBar';
import { useWindowDrag } from './useWindowDrag';
import { restoreTile, tileStyle } from './tiling';
import type { WindowTile } from './tiling';

/**
 * La fenêtre : une barre de titre appartenant au cœur, et un corps (`.wbody`) donné tel quel à ce
 * qu'elle héberge — **sans largeur imposée**, pour qu'une app se rende à n'importe quelle taille.
 *
 * **Plusieurs fenêtres coexistent** (depuis le 7 septembre 2026 ; voir `desk/stack.ts`). Ce
 * composant ne sait rien de ses voisines : tout ce qui la distingue — est-elle devant, quel rang
 * d'empilement, que font ses pastilles — lui arrive par `useWindowFrame()`, que le bureau
 * fournit. Il ne prend donc plus ces informations en propriétés : trois pages les recopiaient à
 * l'identique, et une seule d'entre elles pouvait dire la vérité une fois la pile ouverte.
 *
 * Cliquer **n'importe où** dans une fenêtre la ramène au premier plan (`frame.focus`), y compris
 * dans le contenu de l'app. C'est le geste d'un vrai système, et c'est un `onPointerDown` : il
 * part avant le `click`, donc viser directement la pastille verte d'une fenêtre du fond la met
 * devant *puis* la passe en plein écran, dans cet ordre, en un seul geste.
 *
 * Le plein écran est posé par `Desk`, jamais ici : ce composant ne fait que **suivre**
 * `frame.fullscreen`, qui ne vaut `true` que pour la fenêtre active.
 *
 * On peut la saisir par sa barre de titre et la déplacer (`useWindowDrag`) : le décalage n'est
 * qu'un `transform`, jamais appliqué en plein écran (le déplacement n'y a pas de sens — la
 * fenêtre occupe déjà tout l'écran). Ce décalage vit dans ce composant, qui reste monté tant que
 * la fenêtre est ouverte : une fenêtre déplacée puis passée au second plan retrouve sa place
 * exacte quand elle revient devant.
 *
 * Le même décalage porte la **cascade d'ouverture** (`frame.cascade`) : la première fenêtre est
 * centrée comme dans la référence, les suivantes se posent en escalier. Avant cela, ouvrir une
 * seconde app la superposait exactement à la première, qui disparaissait.
 *
 * Et quel que soit le chemin — cascade, glisser, écran redimensionné, retour du plein écran,
 * retour du dock après avoir été réduite — la fenêtre reste **entièrement à l'écran**, sous la
 * barre de menu et au-dessus du dock. Cet invariant est celui de `window/placement.ts`, et il est
 * testé là.
 */
export interface AppWindowProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

export function AppWindow({ title, subtitle, children }: AppWindowProps): JSX.Element {
  const frame = useWindowFrame();
  const windowRef = useRef<HTMLElement>(null);
  const [side, setSide] = useState<WindowTile | null>(() =>
    restoreTile(readLocal(`${frame.storageKey}:side`, null)),
  );
  const [size, setSize] = useState<{ width: number; height: number } | null>(() => {
    const saved = readLocal<{ width: number; height: number } | null>(
      `${frame.storageKey}:size`,
      null,
    );
    return saved &&
      Number.isFinite(saved.width) &&
      Number.isFinite(saved.height) &&
      saved.width >= 320 &&
      saved.height >= 240
      ? saved
      : null;
  });
  useEffect(() => {
    writeLocal(`${frame.storageKey}:side`, side);
  }, [side, frame.storageKey]);
  useEffect(() => {
    const place = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string; side: unknown }>).detail;
      if (detail?.key === frame.storageKey) setSide(restoreTile(detail.side));
    };
    window.addEventListener('ndrc:window-place', place);
    return () => window.removeEventListener('ndrc:window-place', place);
  }, [frame.storageKey]);
  useEffect(() => {
    const el = windowRef.current;
    if (!el || frame.fullscreen || frame.minimized || side !== null) return;
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth && el.offsetHeight)
        writeLocal(`${frame.storageKey}:size`, { width: el.offsetWidth, height: el.offsetHeight });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [frame.storageKey, frame.fullscreen, frame.minimized, side]);
  const resize = (width: number, height: number) => {
    const el = windowRef.current;
    if (!el) return;
    setSide(null);
    setSize({
      width: Math.max(320, Math.min(window.innerWidth - 24, el.offsetWidth + width)),
      height: Math.max(240, Math.min(window.innerHeight - 176, el.offsetHeight + height)),
    });
  };
  const drag = useWindowDrag(windowRef, {
    fullscreen: frame.fullscreen || side !== null,
    storageKey: frame.storageKey,
    deskFullscreen: frame.deskFullscreen,
    minimized: frame.minimized,
    cascade: frame.cascade,
  });

  const style: CSSProperties = {
    zIndex: frame.zIndex,
    ...(frame.minimized ? { display: 'none' } : undefined),
    // Jamais en plein écran : la fenêtre y occupe déjà tout l'écran (window.css, `[data-fs]
    // .window--front`), un décalage la ferait déborder pour rien.
    ...(frame.fullscreen
      ? undefined
      : side === null
        ? { transform: `translate3d(${drag.offset.x}px, ${drag.offset.y}px, 0)` }
        : undefined),
    ...(!frame.fullscreen && size !== null && side === null
      ? {
          width: Math.min(size.width, window.innerWidth - 24),
          height: Math.min(size.height, window.innerHeight - 176),
        }
      : {}),
    ...(!frame.fullscreen && side !== null
      ? {
          position: 'fixed',
          ...tileStyle(side),
          transform: 'none',
        }
      : {}),
  };

  return (
    <section
      className={frame.active ? 'window window--front' : 'window window--behind'}
      aria-label={title}
      tabIndex={-1}
      style={style}
      ref={windowRef}
      onPointerDown={frame.active ? undefined : frame.focus}
    >
      <div className="window-actions">
        <ActionMenu
          label={t('window.actions')}
          icon="more-horizontal"
          target={windowRef}
          actions={[
            {
              label: t('window.left'),
              icon: 'panel-left',
              run: () => setSide(restoreTile('left')),
            },
            {
              label: t('window.right'),
              icon: 'panel-right',
              run: () => setSide(restoreTile('right')),
            },
            {
              label: t('window.normal'),
              icon: 'window',
              run: () => {
                setSide(null);
                setSize(null);
              },
            },
            { label: t('window.wider'), icon: 'width-expand', run: () => resize(80, 0) },
            { label: t('window.narrower'), icon: 'width-collapse', run: () => resize(-80, 0) },
            { label: t('window.taller'), icon: 'height-expand', run: () => resize(0, 80) },
            { label: t('window.shorter'), icon: 'height-collapse', run: () => resize(0, -80) },
            {
              label: t('window.moveLeft'),
              icon: 'arrow-left',
              run: () => drag.move(-40, 0),
              disabled: side !== null,
            },
            {
              label: t('window.moveRight'),
              icon: 'arrow-right',
              run: () => drag.move(40, 0),
              disabled: side !== null,
            },
            {
              label: t('window.moveUp'),
              icon: 'arrow-up',
              run: () => drag.move(0, -40),
              disabled: side !== null,
            },
            {
              label: t('window.moveDown'),
              icon: 'arrow-down',
              run: () => drag.move(0, 40),
              disabled: side !== null,
            },
            { label: t('window.minimize'), icon: 'minus', run: frame.minimize },
            {
              label: t('window.fullscreen'),
              icon: frame.fullscreen ? 'collapse' : 'expand',
              run: frame.toggleFullscreen,
            },
            { label: t('window.close'), icon: 'close', run: frame.close },
          ]}
        />
      </div>
      <TitleBar
        title={title}
        {...(subtitle === undefined ? {} : { subtitle })}
        fullscreen={frame.fullscreen}
        onClose={frame.close}
        onMinimize={frame.minimize}
        onToggleFullscreen={frame.toggleFullscreen}
        onWindowPointerDown={drag.onPointerDown}
        onWindowPointerMove={drag.onPointerMove}
        onWindowPointerUp={drag.onPointerUp}
      />
      <div className="wbody">{children}</div>
    </section>
  );
}
